import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { enumDocumentType } from '@data-layer/enums/documents/enumDocumentType';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { Observable, ReplaySubject } from 'rxjs';
import { CustomValidator } from '@helpers/custom-validator';
import { finalize, takeUntil } from 'rxjs/operators';
import { environment } from '@env/environment';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';

interface IRadioButtonConfig {
  id: string;
  value: enumDocumentType | string;
  label: string;
}

@Component({
  selector: 'document-add-other',
  templateUrl: './document-add-other.component.html',
  styleUrls: ['./document-add-other.component.scss'],
})
export class DocumentAddOtherComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public includesTypes: enumDocumentType[] = [
    enumDocumentType.TRADE_CERTIFICATE,
    enumDocumentType.PERMIT,
    enumDocumentType.ARRIVAL_NOTICE,
    enumDocumentType.FREIGHT_OTHER,
  ];
  @Input() public id: string = 'add-other-document';
  @Input() public otherPossibleDocuments: DocumentModel[] = [];
  @Input() public createDocument: DocumentCreateModel;
  @Input() public upload: (document: DocumentCreateModel, file: File) => Observable<any>;

  @ViewChild('input', { static: true }) public inputRef: ElementRef;

  public fileControl: UntypedFormControl = new UntypedFormControl(null, [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
  ]);
  public errors = { maxFileSize: 'FORM_ERROR.MAX_FILE_SIZE', fileExtensionNotAllowed: 'FORM_ERROR.FILE_EXTENSION_NOT_ALLOWED' };

  public isModalOpen: boolean = false;
  public control: UntypedFormControl;
  public formConfig: IRadioButtonConfig[] = [];
  public isLoading: boolean = false;
  public ALLOWED_FILE_MIMES: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  public ngOnInit(): void {
    this.initForm();
    this.initConfig();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.otherPossibleDocuments) {
      this.initConfig();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public openChange(open: boolean) {
    this.isModalOpen = open;
  }

  public openModal() {
    this.openChange(true);
  }

  public onAddOtherDocument() {
    this.inputRef.nativeElement.click();
  }

  public handleFiles(event: Event) {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      if (!this.includesTypes.includes(this.control.value)) {
        const value: string = this.control.value.toString();
        const index: number = +value.split('-')[1];
        const document = this.otherPossibleDocuments[index];
        this.isLoading = true;
        this.uploadFile(files[0], (file) => {
          this.upload({ ...document, fileName: file.name }, file)
            .pipe(
              finalize(() => (this.isLoading = false)),
              takeUntil(this.destroy$),
            )
            .subscribe(() => {
              this.openChange(false);
            });
        });
      } else {
        this.isLoading = true;
        this.uploadFile(files[0], (file) => {
          this.upload({ ...this.createDocument, type: this.control.value, fileName: file.name }, file)
            .pipe(
              finalize(() => (this.isLoading = false)),
              takeUntil(this.destroy$),
            )
            .subscribe(() => {
              this.openChange(false);
            });
        });
      }
    }
    this.control.reset(this.formConfig[0].value);
    this.inputRef.nativeElement.value = '';
  }

  public clickInput() {
    this.clearFile();
  }

  private initForm() {
    this.control = new UntypedFormControl(enumDocumentType.PERMIT, Validators.required);
  }

  private initConfig() {
    const config = [
      enumDocumentType.PERMIT,
      enumDocumentType.ARRIVAL_NOTICE,
      enumDocumentType.TRADE_CERTIFICATE,
      enumDocumentType.BILL_OF_LADING,
      enumDocumentType.ISF_FORM,
    ]
      .filter((type) => this.includesTypes.includes(type))
      .map((type) => ({
        id: 'select-type-' + type,
        label: 'DOCUMENT_TYPE.' + enumDocumentType[type],
        value: type as any,
      }));

    if (this.otherPossibleDocuments && this.otherPossibleDocuments.length) {
      this.otherPossibleDocuments.forEach((document, index) => {
        config.push({
          id: 'select-document-' + document.fileName + ' - ' + index,
          label: document.fileName,
          value: 'document-' + index,
        });
      });
    }

    if (this.includesTypes.includes(enumDocumentType.UNKNOWN)) {
      config.push({
        id: 'select-type-' + enumDocumentType.UNKNOWN,
        label: 'DOCUMENT_TYPE.' + enumDocumentType[enumDocumentType.UNKNOWN],
        value: enumDocumentType.UNKNOWN,
      });
    }

    this.formConfig = config;
  }

  private uploadFile(file: File, callBack: (file: File) => void): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();
    if (this.fileControl.valid) {
      callBack(file);
    }
    this.inputRef.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }
}
